import * as React from 'react'
import { castingStoreGetMaxDuration, StoreProps } from '../../../../services/stores/casting.store'
import StepperInputComponent from '../../../shared/stepper-input.component'
import { durationCalculatorItemsI18nValue } from '../../../../services/models/duration-calculator-items.service'
import CastingDurationCalculatorFeedbackComponent from './casting-duration-calculator-feedback.component'

type DurationCalculatorItem = StoreProps['data']['offeredService']['duration_calculator_items'][0]

export default function CastingDurationCalculatorModernComponent ({ store, data, data: { offeredService, selectedDurationCalculatorItems } }: StoreProps) {
  const hasBeTouched = React.useRef(false)
  const orderedCalcItems = React.useMemo(() => offeredService.duration_calculator_items.slice().sort((a, b) => a.position - b.position), [offeredService.duration_calculator_items])

  const computeResult = React.useCallback(() => {
    let sumDuration = 0

    for (const selection of selectedDurationCalculatorItems) {
      const calcItem = offeredService.duration_calculator_items.find(item => item.id === selection.duration_calculator_item_id)
      if (!calcItem) continue

      sumDuration += calcItem.definition.duration_per_unit * (selection.quantity / (calcItem.definition.step ?? 1)) * 3600
    }

    sumDuration = Math.min(Math.max(sumDuration, offeredService.duration_min), castingStoreGetMaxDuration(data))
    sumDuration = Math.ceil(sumDuration / offeredService.duration_step) * offeredService.duration_step
    return sumDuration
  }, [data, offeredService.duration_calculator_items, offeredService.duration_min, offeredService.duration_step, selectedDurationCalculatorItems])

  const updateState = React.useCallback((value: number, calcItem: DurationCalculatorItem) => {
    hasBeTouched.current = true
    const existing = selectedDurationCalculatorItems.find(item => item.duration_calculator_item_id === calcItem.id)
    
    const newSelectedDurationCalculatorItems = existing
      ? selectedDurationCalculatorItems.map(item => item === existing ? { ...item, quantity: value } : item)
      : [ ...selectedDurationCalculatorItems, { id: null, duration_calculator_item_id: calcItem.id, quantity: value } ]

    store.set({ selectedDurationCalculatorItems: newSelectedDurationCalculatorItems })
  }, [selectedDurationCalculatorItems, store])

  React.useEffect(() => {
    const calculatorResult = computeResult()

    if (calculatorResult !== data.calculatorResult || calculatorResult !== data.calculatorResultFirstEvent) {
      store.set({ calculatorResult, calculatorResultFirstEvent: calculatorResult })
      
      // do not reset in cascade after slot has been touched if no change has been applyed to the calculator form yet
      if (hasBeTouched.current) store.initSlotWithDuration(store.getMinDuration())
    }
  }, [computeResult, data.calculatorResult, data.calculatorResultFirstEvent, selectedDurationCalculatorItems, store])

  return <div id="js-test-duration-calculator">
    { orderedCalcItems.map(calcItem => {
      const selectedCalcItem = selectedDurationCalculatorItems.find(item => item.duration_calculator_item_id === calcItem.id)

      return <div key={ calcItem.id }>
        <StepperInputComponent
          name={ `duration-calculator-item-${calcItem.id}` }
          value={ selectedCalcItem?.quantity ?? 0 }
          onChange={ val => updateState(val, calcItem) }
          min={ 0 }
          max={ 10000 }
          step={ calcItem.definition.step ?? 1 }
          formatter={ val => durationCalculatorItemsI18nValue(calcItem.definition, val ?? 0) }
          style={{ marginBottom: '0.4em' }} />
     </div>
    }) }

    <CastingDurationCalculatorFeedbackComponent store={ store } data={ data } resultForAllSlots={ computeResult() } />
  </div>
}
